import * as RadixTabs from '@radix-ui/react-tabs';
import type { ReactNode } from 'react';

export interface TabItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onValueChange: (value: string) => void;
  'aria-label': string;
}

/**
 * An accessible tabbed panel built on Radix (TECH_STACK §16: "Radix UI
 * primitives for dialog/menu/tabs/toast"). Radix handles roving
 * `tabindex`, arrow-key navigation, and `aria-selected`/`aria-controls`
 * wiring (APP_SPEC §36).
 */
export function Tabs({ items, value, onValueChange, ...rest }: TabsProps) {
  return (
    <RadixTabs.Root value={value} onValueChange={onValueChange} className="flex flex-col gap-4">
      <RadixTabs.List
        aria-label={rest['aria-label']}
        className="flex flex-wrap gap-1 border-b border-borderStrong"
      >
        {items.map((item) => (
          <RadixTabs.Trigger
            key={item.value}
            value={item.value}
            className="min-h-touch rounded-t-md border-b-strong border-transparent px-3 py-2 text-sm font-semibold text-textSecondary transition-colors duration-fast ease-standard hover:bg-surfaceAlt hover:text-textPrimary focus-visible:outline-none data-[state=active]:border-accent data-[state=active]:bg-surfaceElevated data-[state=active]:text-textPrimary"
          >
            {item.label}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {items.map((item) => (
        <RadixTabs.Content
          key={item.value}
          value={item.value}
          className="focus-visible:outline-none"
        >
          {item.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}
